import { observable, action, makeAutoObservable  } from 'mobx'
import { deleteUserAsset, fetchImages, fetchTeamAssets, fetchUserImages, uploadImage } from '../services/ElementService'
import { PhotoElement } from './componentStore'

class PhotoStore {

  constructor(errorStore, workAreaStore, authStore) {
    makeAutoObservable(this);
    this.errorStore = errorStore
    this.workAreaStore = workAreaStore
    this.authStore = authStore
  }

  searchTerm = ''
  pageNumber = 1
  userPageNo = 0
  photoItems = {
    col_1: [],
    col_2: []
  }
  userAssets = []
  isEmpty = false
  projectDesign = false
  userAssetsFetched = false
  uploading = false
  projectAssets = []
  projectAssetsFetched = false
  projectAssetsLoading = false

  teamAssets = []
  teamAssetsFetched = false
  teamAssetsLoading = false
  teamPageNo = 0
  teamEmpty = false

  projectPageNo = 0
  projectEmpty = false
  progress = 0
  apiLoading = false
  apiLoaded = false
  userAssetsLoading = false

  setTerm (term) {
    this.apiLoaded = false
    this.apiLoading = true
    this.pageNumber = 1
    this.searchTerm = term
    this.photoItems = {
      col_1: [],
      col_2: []
    }
  }

  async searchImages(initFetch = false){
    try{
      this.apiLoading = true
      const res = await fetchImages(this.searchTerm, this.pageNumber, '')
      res.images.forEach((img, i) => {
        if(i % 2 === 0){
          this.photoItems.col_1.push({...img, col: 'col_1'})
        }else{
          this.photoItems.col_2.push({...img, col: 'col_2'})
        }
      })
      if(initFetch){
        this.apiLoaded = true
      }
    }catch(err){
      console.log(err)
      this.errorStore.setErrorMessage('Could not load images', true)
    }finally{
      this.apiLoading = false
    }
  }

  async deleteAsset(assetId){
    const assetIdx = this.userAssets.findIndex(({ assetId: id }) => id === assetId)
    const projectAssetIdx = this.projectAssets.findIndex(({ assetId: id }) => id === assetId)
    let copy
    let projectCopy
    if(assetIdx > -1){
      copy = {...this.userAssets[assetIdx]}
    }
    if(projectAssetIdx > -1) {
      projectCopy = {...this.projectAssets[projectAssetIdx]}
    }
    try{
      this.userAssets = this.userAssets.filter(({ assetId: id }) => id !== assetId)
      this.projectAssets = this.projectAssets.filter(({ assetId: id }) => id !== assetId)
      await deleteUserAsset(assetId)
      this.errorStore.setErrorMessage('Deleted asset', false)
      return true
    }catch(err){
      this.userAssets.splice(assetIdx, 0, copy)
      if(projectCopy){
        this.projectAssets.splice(projectAssetIdx, 0, projectCopy)
      }
      this.errorStore.setErrorMessage('Failed to delete asset', true)
    }
  }

  setFetchStatus(status){
    this.userAssetsFetched = status
  }

  async fetchTeamAssets(){
    if(this.authStore.isTeam && !this.teamAssetsLoading){
      try{
        this.teamAssetsLoading = true
        const { data } = await fetchTeamAssets(this.authStore.teamDetails._id, this.teamPageNo)
        this.teamEmpty = data.isEmpty
        this.teamAssets = [...this.teamAssets, ...data.images]
        this.teamPageNo += 1
        this.teamAssetsLoading = false
        this.teamAssetsFetched = true
      }catch(err){
        this.errorStore.setErrorMessage('Failed to fetch team assets', true)
      }finally {
        this.teamAssetsLoading = false
      }
    }
  }

  async fetchUserImages(project = false, projectChanged = false){
    if(project){
      this.projectDesign = project
    }
    if(project && projectChanged){
      this.projectAssets = []
      this.projectPageNo = 0
      this.projectEmpty = false
    }
    try{
      if((!this.isEmpty && !project) || (!this.projectEmpty && project)){
        if(project){
          this.projectAssetsLoading = true
        }else{
          this.userAssetsLoading = true
        }
        const res = await fetchUserImages(project ? this.projectPageNo : this.userPageNo, project ? project._id : project)
        if(project){
          this.projectEmpty = res.data.isEmpty
          this.projectAssets = [...this.projectAssets, ...res.data.images]
          this.projectAssetsFetched = true
          this.projectPageNo += 1
          this.projectAssetsLoading = false
        }else{
          this.isEmpty = res.data.isEmpty
          this.userAssets = [...this.userAssets, ...res.data.images]
          this.userAssetsFetched = true
          this.userPageNo += 1
        }
      }
    }catch(err){
      console.log(err)
      this.errorStore.setErrorMessage('Could not load uploads', true)
    }finally {
      this.userAssetsLoading = false
      this.projectAssetsLoading = false
    }
  }

  setImageLoading(imgId, col, project = false, team = false){
    if(project){
      const idx = this.projectAssets.findIndex(({ assetId }) => assetId === imgId)
      if(idx > -1){
        this.projectAssets[idx].loading = false
      }
    }
    if(team){
      const idx = this.teamAssets.findIndex(({ assetId }) => assetId === imgId)
      if(idx > -1){
        this.teamAssets[idx].loading = false
      }
    }
    if(!project && !col){
      const idx = this.userAssets.findIndex(({ assetId }) => assetId === imgId)
      if(idx > -1){
        this.userAssets[idx].loading = false
      }
    }
    if(col){
      let idx = this.photoItems[col].findIndex(({ id }) => id === imgId)
      if(idx > -1){
        this.photoItems[col][idx].loading = false
      }
    }
  }

  handleLoadError(imgId, project = false, team = false){
    if(project){
      const idx = this.projectAssets.findIndex(({ assetId }) => assetId === imgId)
      if(idx > -1){
        this.projectAssets.splice(idx, 1)
      }
    }
    if(team){
      const idx = this.teamAssets.findIndex(({ assetId }) => assetId === imgId)
      if(idx > -1){
        this.teamAssets.splice(idx, 1)
      }
    }
    const idx = this.userAssets.findIndex(({ assetId }) => assetId == imgId)
    if(idx > -1){
      this.userAssets.splice(idx, 1)
    }
  }

  increasePage(){
    this.pageNumber += 1
  }

  initProgress(){
    this.progress = 0
  }

  setProgress(data){
    this.progress = Math.round((100 * data.loaded) / data.total)
  }

  async uploadImage(file, designId, project = false){
    try{
      this.progress = 0
      this.uploading = true
      const data = await uploadImage(file, designId, this.setProgress.bind(this), null, project ? project._id : undefined)
      if(this.authStore.sharedWithTeam){
        this.teamAssets.splice(0, 0, data)
      }
      if(!project){
        this.userAssets.splice(0, 0, data)
      }else{
        this.projectAssets.splice(0, 0, data)
      }
      this.errorStore.setErrorMessage('Image uploaded', false)
      this.uploading = false
      this.progress = 0
      return { ...data }
    }catch(err){
      console.log(err)
      this.errorStore.setErrorMessage('Could not upload image', true)
      this.uploading = false
      this.progress = 0
      return false
    }
  }

}

export default PhotoStore